// Company Logo Service - Handles logo upload operations
import { apiClient } from '@/api/core';
import { API_CONFIG } from '@/api/core/apiConfig';
import type { ApiResponse } from '@/api/shared';
import type {
  LC_UpdateCompanyLogoIn,
  LC_UpdateCompanyLogoOut,
  UpdateCompanyLogoResult,
} from '../types/companyLogo.types';
import {
  mapToUpdateCompanyLogoIn,
  mapUpdateCompanyLogoOutToResult,
  createLogoUpdateErrorResult,
} from '../mappers/companyLogo.mapper';

/**
 * Service for company logo operations
 */
export class CompanyLogoService {
  /**
   * Updates the logo for a specific company
   * @param companyId - The company ID
   * @param logoDataUrl - Base64 data URL of the cropped logo
   * @returns Result of the update operation
   */
  async updateLogo(
    companyId: number,
    logoDataUrl: string,
  ): Promise<UpdateCompanyLogoResult> {
    try {
      // Map to transport DTO
      const input: LC_UpdateCompanyLogoIn = mapToUpdateCompanyLogoIn(companyId, logoDataUrl);

      // Call API (apiClient adds versioning automatically)
      const response = await apiClient.put<LC_UpdateCompanyLogoOut>(
        API_CONFIG.ENDPOINTS.COMPANY.UPDATE_LOGO(companyId),
        input,
      );

      // Handle error response
      if (!response.success || !response.data) {
        return createLogoUpdateErrorResult(
          response.error?.description || 'Error al actualizar el logo de la empresa',
        );
      }

      // Map successful response
      return mapUpdateCompanyLogoOutToResult(response.data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      return createLogoUpdateErrorResult(errorMessage);
    }
  }

  /**
   * Raw API call for logo update (returns ApiResponse for advanced use cases)
   * @param companyId - The company ID
   * @param input - Transport DTO
   * @returns API response
   */
  async updateLogoRaw(
    companyId: number,
    input: LC_UpdateCompanyLogoIn,
  ): Promise<ApiResponse<LC_UpdateCompanyLogoOut>> {
    return apiClient.put<LC_UpdateCompanyLogoOut>(
      API_CONFIG.ENDPOINTS.COMPANY.UPDATE_LOGO(companyId),
      input,
    );
  }
}

export const companyLogoService = new CompanyLogoService();
